/**
 * Folha inferior (bottom sheet) das camadas, no celular. Dois modos com papéis
 * que não se confundem (ver docs/proposta-celular-camadas.md):
 *
 * - **Camadas** (`sheetStage = 'camadas'`, o padrão): grade de blocos, um por
 *   painel registrado. Tocar no bloco liga/desliga a camada inteira; a
 *   engrenagem ao lado abre os ajustes. Não-modal: o mapa acima continua
 *   visível e tocável, e a folha fica logo acima da barra de tempo.
 * - **Ajustes** (`half`/`full`): o painel completo de uma camada (sub-produtos,
 *   aparência, legenda), com voltar, o interruptor e fechar. Modal. A alça
 *   alterna as duas alturas.
 *
 * Escape, o toque no fundo escuro e o voltar do celular descem um nível
 * (ajustes → camadas → fechada); o ✕ fecha tudo.
 *
 * Abre pelo "⚙ Camadas: ajustes" no topo do trilho de camadas (ui/TrilhoDeCamadas),
 * que liga e desliga cada camada sem abrir a folha; o antigo mini-botão
 * "Camadas" da coluna da direita saiu (08/10/2026). Ao fechar, o foco volta ao "⚙".
 */
import clsx from 'clsx';
import { Check, ChevronLeft, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useUiStore } from '../state/ui';
import { useModalLayer } from './modalStack';
import { marcarVisto, useEhNovidade } from './novidade';
import { getPanel, getPanels, type PanelDef } from './panels';
import { PanelContent } from './PanelTabs';
import { IconButton, Switch } from './primitives';
import { useSheetHistory } from './sheetHistory';
import { DEFAULT_TONE, TONE_CHIP, TONE_ICON } from './tone';

/** Guardar a dica ("⚙ abre os ajustes") até a primeira vez que a pessoa abre um ajuste. */
const DICA_KEY = 'amaview:dica-ajustes';

function dicaVista(): boolean {
  try {
    return globalThis.localStorage?.getItem(DICA_KEY) === '1';
  } catch {
    return false;
  }
}

function marcarDicaVista(): void {
  try {
    globalThis.localStorage?.setItem(DICA_KEY, '1');
  } catch {
    // navegação privada / armazenamento bloqueado: a dica só volta a aparecer
  }
}

/** Quantas camadas estão ligadas (blocos acesos), para o cabeçalho. */
function useLayersOn(): number {
  // A lista é estável após a inicialização, então a ordem dos hooks não muda.
  return getPanels().reduce((n, p) => n + (p.layer?.useOn() ? 1 : 0), 0);
}

function contagem(n: number): string {
  if (n === 0) return 'nenhuma ligada';
  return n === 1 ? '1 ligada' : `${n} ligadas`;
}

/** Fecha tudo; a próxima abertura começa na grade. */
function fecharFolha(): void {
  useUiStore.getState().set({ sheetOpen: false, sheetStage: 'camadas' });
}

function voltarAsCamadas(): void {
  useUiStore.getState().set({ sheetStage: 'camadas' });
}

/** Rótulo longo ("Pluviômetros") encolhe um pouco em vez de ser cortado em 360 px. */
function tamanhoDoRotulo(label: string): string {
  return label.length > 10 ? 'text-[12px] tracking-tight' : 'text-[13px]';
}

/** Ponto amarelo de aviso (dados indisponíveis) no canto do bloco. */
function Aviso(props: { def: PanelDef }) {
  const warning = props.def.useWarning?.() ?? false;
  if (warning) {
    return (
      <span
        className="absolute top-1 left-1 size-2 rounded-full bg-yellow-300 ring-2 ring-ink-900"
        role="img"
        aria-label="dados indisponíveis"
        title="Dados indisponíveis no momento"
      />
    );
  }
  return null;
}

/**
 * Etiqueta "novo" na linha de resumo. Na grade o verde quer dizer "ligada",
 * então a novidade não pode ser um ponto verde (o trilho do desktop usa um).
 */
function Novo(props: { def: PanelDef }) {
  const novidade = useEhNovidade(props.def.id, props.def.novoAte);
  if (!novidade) return null;
  return (
    <span
      className="pointer-events-none absolute -top-1.5 right-12 rounded-sm bg-white px-1 text-[10px] leading-3 font-semibold text-ink-950 ring-2 ring-ink-900"
      title="Camada nova"
    >
      novo
    </span>
  );
}

/**
 * Bloco de uma camada: o corpo é o interruptor; a engrenagem, à direita e
 * separada por um filete, abre os ajustes. São botões irmãos (não aninhados).
 */
function LayerTile(props: { def: PanelDef; onOpenSettings: (id: string) => void; announce: (msg: string) => void }) {
  const { def, onOpenSettings, announce } = props;
  // `def` é fixo por instância (chave = id), então a ordem dos hooks não muda.
  const on = def.layer?.useOn() ?? false;
  const summary = def.useSummary?.() ?? null;
  const descId = useId();
  const novidade = useEhNovidade(def.id, def.novoAte);
  const tone = def.tone ?? DEFAULT_TONE;
  const Icon = def.icon;

  // Painel que só configura (sem camada própria): o bloco inteiro abre os ajustes.
  if (!def.layer) {
    return (
      <div className="relative flex h-14 min-w-0 rounded-xl border border-ink-700 bg-ink-850">
        <button
          type="button"
          data-tile-gear={def.id}
          onClick={() => onOpenSettings(def.id)}
          aria-label={`Ajustes: ${def.label}`}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-xl pr-2 pl-2.5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400"
        >
          <Icon className="size-5 shrink-0 text-ink-300" aria-hidden />
          <span className={clsx('min-w-0 flex-1 truncate font-semibold text-ink-100', tamanhoDoRotulo(def.label))}>{def.label}</span>
          <SlidersHorizontal className="size-4 shrink-0 text-ink-300" aria-hidden />
        </button>
        <Aviso def={def} />
      <Novo def={def} />
      </div>
    );
  }

  const toggle = () => {
    marcarVisto(def.id);
    def.layer!.setOn(!on);
    announce(`${def.label} ${on ? 'desligada' : 'ligada'}`);
  };

  return (
    <div
      className={clsx(
        'relative flex h-14 min-w-0 rounded-xl border transition-colors [@media(max-height:420px)]:h-12',
        on ? TONE_CHIP[tone] : 'border-ink-700 bg-ink-850',
      )}
    >
      <button
        type="button"
        onClick={toggle}
        aria-pressed={on}
        // Nome estável ("Nível"); o estado vem do aria-pressed e o resumo, da descrição.
        aria-label={def.label}
        aria-describedby={descId}
        title={`${def.label} · toque para ${on ? 'desligar' : 'ligar'}`}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-l-xl pr-1 pl-2.5 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400"
      >
        <span className="relative shrink-0">
          <Icon className={clsx('size-5', on ? TONE_ICON[tone] : 'text-ink-400')} aria-hidden />
          {/* Marca de "ligada" além da cor (legível para quem não distingue as cores). */}
          {on && (
            <span className="absolute -right-1.5 -bottom-1 grid size-3.5 place-items-center rounded-full bg-white ring-2 ring-ink-900" aria-hidden>
              <Check className="size-2.5 text-ink-950" strokeWidth={3.5} />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className={clsx('block truncate leading-tight font-semibold', tamanhoDoRotulo(def.label), on ? 'text-white' : 'text-ink-200')}>
            {def.label}
          </span>
          <span id={descId} className={clsx('flex min-w-0 items-center text-[11px] leading-tight', on ? 'text-ink-100' : 'text-ink-300')}>
            <span className="truncate">{on ? (summary ?? 'ligada') : 'desligada'}</span>
            {/* A etiqueta "NOVO" fica fora do botão; o leitor de tela ouve aqui. */}
            {novidade && <span className="sr-only"> · camada nova</span>}
          </span>
        </span>
      </button>
      <button
        type="button"
        data-tile-gear={def.id}
        onClick={() => onOpenSettings(def.id)}
        aria-label={`Ajustes: ${def.label}`}
        title={`Ajustes: ${def.label}`}
        className={clsx(
          'flex w-11 shrink-0 items-center justify-center rounded-r-xl border-l focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400',
          on ? 'border-current/25 text-ink-100' : 'border-ink-700 text-ink-300',
        )}
      >
        <SlidersHorizontal className="size-4" aria-hidden />
      </button>
      <Aviso def={def} />
      <Novo def={def} />
    </div>
  );
}

/**
 * Modo 1: a grade de camadas. `focusGear` é a camada cujos ajustes acabaram
 * de fechar (pelo ‹, Escape, fundo ou voltar do celular): o foco volta à
 * engrenagem dela; ao abrir a folha, vai para o título.
 */
function CamadasView(props: { announce: (msg: string) => void; focusGear: string | null }) {
  const set = useUiStore((s) => s.set);
  const count = useLayersOn();
  const [dica, setDica] = useState(() => !dicaVista());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Ao abrir, o foco vai para o título; ao voltar dos ajustes, para a engrenagem de onde se saiu.
  useEffect(() => {
    const id = props.focusGear;
    const gears = gridRef.current?.querySelectorAll<HTMLElement>('[data-tile-gear]') ?? [];
    const gear = id ? [...gears].find((g) => g.dataset.tileGear === id) : undefined;
    (gear ?? headingRef.current)?.focus({ preventScroll: true });
    // Só na montagem: depois disso o foco é da pessoa.
  }, []);

  const openSettings = (id: string) => {
    marcarVisto(id);
    if (dica) {
      marcarDicaVista();
      setDica(false);
    }
    set({ tab: id, sheetStage: 'half' });
  };

  return (
    <>
      <div className="flex items-center gap-2 pt-1.5 pr-1.5 pl-3.5">
        <h2 ref={headingRef} tabIndex={-1} className="text-sm font-semibold text-white outline-none">
          Camadas
        </h2>
        <span className="text-xs text-ink-300 tabular-nums">{contagem(count)}</span>
        <span className="flex-1" />
        <IconButton icon={X} label="Fechar" size="lg" onClick={fecharFolha} />
      </div>
      {dica && (
        <p className="-mt-1 px-3.5 pb-1.5 text-[11px] text-ink-300 [@media(max-height:420px)]:hidden">
          Toque para ligar ou desligar ·{' '}
          <SlidersHorizontal className="inline size-3 align-[-2px]" aria-label="engrenagem" /> abre os ajustes
        </p>
      )}
      <div
        ref={gridRef}
        role="group"
        aria-label="Camadas"
        // A borda de baixo esmaece: quando a grade rola (paisagem), a linha cortada
        // sugere que há mais; sem rolagem, o esmaecido cai no respiro.
        className="scroll-thin grid min-h-0 grid-cols-2 gap-1.5 overflow-y-auto overscroll-contain px-2 pt-2 pb-3 [mask-image:linear-gradient(to_bottom,black_calc(100%-12px),transparent)] min-[560px]:grid-cols-3"
      >
        {getPanels().map((def) => (
          <LayerTile key={def.id} def={def} onOpenSettings={openSettings} announce={props.announce} />
        ))}
      </div>
    </>
  );
}

/** Título e interruptor dos ajustes. Remonta ao trocar de camada (chave = id). */
function AjustesTitle(props: { def: PanelDef; headingRef: RefObject<HTMLHeadingElement | null>; announce: (msg: string) => void }) {
  const { def } = props;
  const on = def.layer?.useOn() ?? false;
  const Icon = def.icon;
  return (
    <>
      <Icon className={clsx('size-5 shrink-0', on ? TONE_ICON[def.tone ?? DEFAULT_TONE] : 'text-ink-400')} aria-hidden />
      <h2 ref={props.headingRef} tabIndex={-1} className="min-w-0 truncate text-base font-semibold text-white outline-none">
        {def.label}
      </h2>
      {def.layer && (
        <Switch
          checked={on}
          onChange={(v) => {
            def.layer!.setOn(v);
            props.announce(`${def.label} ${v ? 'ligada' : 'desligada'}`);
          }}
          label={`${def.label}: ${on ? 'desligar' : 'ligar'} camada`}
          // Alvo de toque maior que o desenho (44 px).
          className="ml-1 before:absolute before:-inset-3 before:content-['']"
        />
      )}
    </>
  );
}

/** Modo 2: ajustes de uma camada. */
function AjustesView(props: { announce: (msg: string) => void }) {
  const tab = useUiStore((s) => s.tab);
  const stage = useUiStore((s) => s.sheetStage);
  const set = useUiStore((s) => s.set);
  const def = getPanel(tab) ?? getPanels()[0];
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [tab]);

  if (!def) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => set({ sheetStage: stage === 'full' ? 'half' : 'full' })}
        aria-label={stage === 'full' ? 'Reduzir o painel' : 'Expandir o painel'}
        className="flex h-6 w-full shrink-0 items-center justify-center pt-1 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400"
      >
        <span className="h-1 w-10 rounded-full bg-ink-500" aria-hidden />
      </button>
      <div className="flex items-center gap-2 border-b border-ink-700/70 pr-1.5 pb-1.5 pl-1">
        <IconButton icon={ChevronLeft} label="Voltar às camadas" size="lg" onClick={voltarAsCamadas} />
        <AjustesTitle key={def.id} def={def} headingRef={headingRef} announce={props.announce} />
        <span className="flex-1" />
        <IconButton icon={X} label="Fechar" size="lg" onClick={fecharFolha} />
      </div>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <PanelContent />
      </div>
    </>
  );
}

/** Altura atual da barra de tempo (a grade fica logo acima dela). */
function useTimeBarHeight(active: boolean): number {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    if (!active) return;
    const el = document.querySelector<HTMLElement>('[data-timebar]');
    if (!el) return;
    const update = () => setH(Math.round(el.getBoundingClientRect().height));
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);
  return h;
}

export function MobileSheet() {
  const open = useUiStore((s) => s.sheetOpen);
  const stage = useUiStore((s) => s.sheetStage);
  const tab = useUiStore((s) => s.tab);
  const layerRef = useRef<HTMLDivElement>(null);
  const camadas = stage === 'camadas';
  const timeBarHeight = useTimeBarHeight(open && camadas);
  const [anuncio, setAnuncio] = useState('');
  useSheetHistory(layerRef);
  // Vindo dos ajustes (por qualquer caminho), a grade devolve o foco à engrenagem.
  const prevStage = useRef(stage);
  const focusGear = camadas && prevStage.current !== 'camadas' ? tab : null;
  useEffect(() => {
    prevStage.current = stage;
  }, [stage]);

  // Uma camada só na pilha: modal nos ajustes (Escape volta às camadas, o mapa
  // fica `inert`), não-modal na grade (Escape fecha, o mapa segue livre).
  useModalLayer(open, { modal: !camadas, onEscape: camadas ? fecharFolha : voltarAsCamadas, ref: layerRef });

  useEffect(() => {
    if (!open) setAnuncio('');
  }, [open]);

  if (!open) return null;
  const def = getPanel(tab);

  // Portal para o <body> (fora de #root, que fica `inert` com os ajustes abertos).
  return createPortal(
    <div ref={layerRef}>
      {!camadas && <div className="fixed inset-0 z-40 bg-ink-950/30" onClick={voltarAsCamadas} aria-hidden />}
      <div
        role={camadas ? 'region' : 'dialog'}
        aria-modal={camadas ? undefined : 'true'}
        aria-label={camadas ? 'Camadas' : `Ajustes: ${def?.label ?? 'camada'}`}
        data-sheet-stage={stage}
        // Na grade, até ~60 % do espaço acima da barra de tempo (em paisagem a grade rola por dentro).
        style={camadas ? { bottom: timeBarHeight, maxHeight: `calc((100dvh - ${timeBarHeight}px) * 0.6)` } : undefined}
        className={clsx(
          'fixed inset-x-0 z-50 flex flex-col rounded-t-2xl border-t border-ink-600 bg-ink-900 pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)] shadow-2xl outline-none',
          camadas ? '' : 'bottom-0 pb-[env(safe-area-inset-bottom)]',
          stage === 'full' ? 'max-h-[92dvh] min-h-[92dvh]' : stage === 'half' ? 'max-h-[62dvh]' : '',
        )}
      >
        {camadas ? <CamadasView announce={setAnuncio} focusGear={focusGear} /> : <AjustesView announce={setAnuncio} />}
      </div>
      <p className="sr-only" aria-live="polite">
        {anuncio}
      </p>
    </div>,
    document.body,
  );
}
